import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { inr, num, day, dt, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { WEEK_DAYS } from './Employees';
import PayslipView from '../components/PayslipView';
import Modal from '../components/Modal';
import { ADVANCE_KINDS, advanceKind } from '../utils/advances';

// Adjust an advance's outstanding up (give more) or down (waive / repaid outside payroll).
const AdjustLoanModal = ({ loan, onClose, onSaved }) => {
  const [dir, setDir] = useState('up');
  const [amt, setAmt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const remaining = Number(loan.amount) - Number(loan.recovered);
  const a = Number(amt) || 0;
  const delta = dir === 'up' ? a : -a;
  const newRemaining = remaining + delta;
  const save = async () => {
    if (!(a > 0)) return toast.error('Enter an amount');
    setBusy(true);
    try { await api.post(`/employees/loans/${loan.id}/adjust`, { delta, note }); toast.success('Advance adjusted'); onSaved(); onClose(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Adjust advance" onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy || !(a > 0)}>{busy ? 'Saving…' : 'Apply'}</button></>}>
      <p className="muted small" style={{ marginTop: 0 }}>Outstanding: <strong>{inr(remaining)}</strong> · recovered {inr(loan.recovered)}.</p>
      <div className="row">
        <div className="field"><label>Direction</label><select value={dir} onChange={(e) => setDir(e.target.value)}><option value="up">Increase (give more)</option><option value="down">Reduce (waive / repaid)</option></select></div>
        <div className="field"><label>Amount (₹)</label><input type="number" min="0" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} autoFocus /></div>
      </div>
      <div className="field"><label>Note</label><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional)" /></div>
      {a > 0 && <p className="muted small">New outstanding: <strong>{inr(newRemaining)}</strong>{newRemaining <= 0.001 ? ' · closes the advance' : ''}</p>}
    </Modal>
  );
};

const todayStr = () => new Date().toISOString().slice(0, 10);
// "July 2026" — the last month a leaver still appears in, said in the words the
// month pickers on Attendance and Payroll use.
const monthName = (d) => (d
  ? new Date(`${String(d).slice(0, 7)}-01`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
  : 'the month they left');

// Uploaded docs are served at <host>/uploads/… (outside the /api path).
const FILE_HOST = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
const fileSrc = (u) => (u ? (u.startsWith('http') ? u : `${FILE_HOST}${u}`) : null);
const isImg = (u) => /\.(png|jpe?g|webp|gif)$/i.test(u || '');

const LEDGER_KINDS = [
  ['salary_advance', 'Salary advance (deduct)'],
  ['item', 'Items taken from branch (deduct)'],
  ['deduction', 'Other deduction'],
  ['bonus', 'Bonus / addition'],
];
const kindLabel = (k) => (LEDGER_KINDS.find((x) => x[0] === k) || [k, k])[1];
const kindBadge = (k) => ({ off: 'badge-blue', salary_advance: 'badge-gold', item: 'badge-gold', deduction: 'badge-red', bonus: 'badge-green' }[k] || 'badge-gray');

// One document row: shows the current doc + an upload control.
const DocUploader = ({ label, field, url, onUpload, canEdit }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    try { await onUpload(field, file); toast.success(`${label} uploaded`); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div className="doc-row">
      <div className="doc-label">{label}</div>
      <div className="doc-view">
        {url ? (isImg(url)
          ? <a href={fileSrc(url)} target="_blank" rel="noreferrer"><img src={fileSrc(url)} alt={label} className="doc-thumb" /></a>
          : <a href={fileSrc(url)} target="_blank" rel="noreferrer" className="link-strong">View file</a>)
          : <span className="muted small">Not uploaded</span>}
      </div>
      {canEdit && <label className="btn btn-outline btn-sm">{busy ? 'Uploading…' : (url ? 'Replace' : 'Upload')}<input type="file" accept="image/*,application/pdf" hidden onChange={onFile} disabled={busy} /></label>}
    </div>
  );
};

const EmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can('employees:write');
  const isOwner = can('*');   // only the owner may delete a finalized payslip
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewSlip, setViewSlip] = useState(null);
  const [loan, setLoan] = useState({ kind: 'personal', amount: '', months: 6, startMonth: new Date().toISOString().slice(0, 7), note: '' });
  const [entry, setEntry] = useState({ date: new Date().toISOString().slice(0, 10), kind: 'salary_advance', amount: '', note: '' });
  const [locations, setLocations] = useState([]);
  const [adjustLoan, setAdjustLoan] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/employees/${id}`).then(({ data }) => setData(data))
      .catch(() => { toast.error('Employee not found'); navigate('/employees'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);
  useEffect(load, [load]);
  useEffect(() => { api.get('/locations').then(({ data }) => setLocations(data.locations || [])).catch(() => {}); }, []);

  const del = async (hard) => {
    if (!window.confirm(hard ? 'Permanently DELETE this employee and their attendance / advances? This cannot be undone.' : 'Deactivate this employee?')) return;
    try { const { data: r } = await api.delete(`/employees/${id}${hard ? '?hard=1' : ''}`); toast.success(r.message); if (hard) navigate('/employees'); else load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (loading) return <div className="spinner" />;
  if (!data) return null;
  const { employee: e, loans, ledger, payslips } = data;

  // Totals for the two money tables below. Advances: only ACTIVE ones still take an
  // installment each month. Ledger: bonuses add to pay, everything else deducts, and
  // "open" is what the next payroll will actually pick up (nothing settled yet).
  const sumBy = (arr, f) => arr.reduce((s, x) => s + (Number(f(x)) || 0), 0);
  const activeLoans = loans.filter((l) => l.status === 'active');
  const loanTotals = {
    amount: sumBy(loans, (l) => l.amount),
    installment: sumBy(activeLoans, (l) => l.monthlyInstallment),
    recovered: sumBy(loans, (l) => l.recovered),
    remaining: sumBy(loans, (l) => Number(l.amount) - Number(l.recovered)),
  };
  const entries = ledger.filter((x) => x.kind !== 'off');
  const ofKind = (k, rows = entries) => sumBy(rows.filter((x) => x.kind === k), (x) => x.amount);
  const open = entries.filter((x) => !x.settledInPayslipId);
  const signed = (rows) => ofKind('salary_advance', rows) + ofKind('item', rows) + ofKind('deduction', rows) - ofKind('bonus', rows);
  const ledgerTotals = { net: signed(entries), openNet: signed(open), advances: ofKind('salary_advance'), items: ofKind('item'), deductions: ofKind('deduction'), bonus: ofKind('bonus') };

  const startEdit = () => { setForm({ ...e, exitDate: e.exitDate ? String(e.exitDate).slice(0, 10) : '' }); setEdit(true); };
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  // Marking someone inactive offers today as their last working day — the common
  // case, and a date that can be corrected beats an empty box that cannot be
  // saved. Bringing them back clears it: they have not left.
  const setStatus = (v) => setForm((p) => ({
    ...p,
    status: v,
    exitDate: v === 'inactive' ? (p.exitDate || todayStr()) : '',
  }));
  const save = async () => {
    // A blank name would leave a row nobody can find again — it is how every
    // other screen refers to this person.
    if (!String(form.name || '').trim()) return toast.error('Name cannot be empty');
    // The API refuses this too; catching it here keeps the date box on screen
    // with the cursor in it rather than bouncing an error off a closed form.
    if (form.status === 'inactive' && !form.exitDate) return toast.error('Set an end date — it decides which months they still appear in');
    if (form.status === 'inactive' && form.joinDate && form.exitDate < String(form.joinDate).slice(0, 10)) {
      return toast.error('End date cannot be before the join date');
    }
    setSaving(true);
    try {
      const { data: r } = await api.put(`/employees/${id}`, {
        ...form,
        name: String(form.name).trim(),
        code: String(form.code || '').trim() || undefined,
        monthlySalary: Number(form.monthlySalary) || 0,
        monthlyOffAllowance: Number(form.monthlyOffAllowance) || 0,
        offBalance: Number(form.offBalance) || 0,
      });
      setData((d) => ({ ...d, employee: r.employee })); setEdit(false); toast.success('Saved');
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const uploadDoc = async (field, file) => {
    const fd = new FormData(); fd.append(field, file);
    const { data: r } = await api.post(`/employees/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setData((d) => ({ ...d, employee: r.employee }));
  };

  const addLoan = async () => {
    if (!(Number(loan.amount) > 0)) return toast.error('Enter a loan amount');
    try { await api.post(`/employees/${id}/loans`, { ...loan, amount: Number(loan.amount), months: Number(loan.months) }); setLoan({ kind: 'personal', amount: '', months: 6, startMonth: new Date().toISOString().slice(0, 7), note: '' }); load(); toast.success('Advance added'); }
    catch (err) { toast.error(apiError(err)); }
  };
  const delLoan = async (lid) => { try { await api.delete(`/employees/loans/${lid}`); load(); } catch (err) { toast.error(apiError(err)); } };

  const addEntry = async () => {
    if (!(Number(entry.amount) > 0)) return toast.error('Enter an amount');
    try { await api.post(`/employees/${id}/ledger`, { ...entry, amount: Number(entry.amount) || 0 }); setEntry({ date: new Date().toISOString().slice(0, 10), kind: entry.kind, amount: '', note: '' }); load(); toast.success('Entry added'); }
    catch (err) { toast.error(apiError(err)); }
  };
  const delEntry = async (eid) => { try { await api.delete(`/employees/ledger/${eid}`); load(); } catch (err) { toast.error(apiError(err)); } };

  // Owner-only: undo a finalized payslip. Rewinds the off balance, un-recovers the
  // loan installment and reopens that month's ledger entries so payroll can re-run.
  const delSlip = async (p) => {
    if (!window.confirm(`Delete the ${p.month} payslip for ${e.name}?

This reverses the off balance, un-recovers the loan installment and reopens the ledger entries for that month. Payroll can then be run again.`)) return;
    try { const { data } = await api.delete(`/employees/payslips/${p.id}`); toast.success(data.message); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const val = (k, extra) => (edit
    ? <input value={form[k] ?? ''} onChange={(ev) => setF(k, ev.target.value)} {...extra} />
    : (e[k] || e[k] === 0 ? e[k] : '—'));

  return (
    <div>
      <div className="section-head">
        <div className="flex gap" style={{ alignItems: 'center' }}>
          <Link to="/employees" className="muted">‹ Employees</Link>
          {e.photoUrl && <img src={fileSrc(e.photoUrl)} alt={e.name} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />}
          {/* The name and code were the only details with no field behind them,
              so a misspelling on the day someone joined was permanent. */}
          {edit
            ? <input className="h1-edit" style={{ maxWidth: 320 }} value={form.name ?? ''} onChange={(ev) => setF('name', ev.target.value)} placeholder="Employee name" autoFocus />
            : <h2 style={{ margin: 0 }}>{e.name}</h2>}
          {edit
            ? <input style={{ width: 120 }} value={form.code ?? ''} onChange={(ev) => setF('code', ev.target.value)} placeholder="Code" title="Their staff code — leave as it is unless it is wrong" />
            : (e.code && <span className="chip-static">{e.code}</span>)}
          <span className={`badge ${e.status === 'active' ? 'badge-green' : 'badge-gray'}`}
            title={e.status !== 'active' && e.exitDate ? `Last worked ${day(e.exitDate)} — shown on months up to ${monthName(e.exitDate)}` : undefined}>
            {e.status}{e.status !== 'active' && e.exitDate ? ` · left ${day(e.exitDate)}` : ''}
          </span>
        </div>
        {canEdit && (edit
          ? <div className="flex gap"><button className="btn btn-outline" onClick={() => setEdit(false)}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button></div>
          : <div className="flex gap"><button className="btn btn-outline" onClick={startEdit}>Edit</button><button className="btn btn-danger" onClick={() => del(true)} title="Permanently delete (or deactivate if they have payslips)">Delete</button></div>)}
      </div>

      <div className="prod-cols">
        {/* Details + payroll settings */}
        <div className="card card-pad">
          <h3 style={{ marginTop: 0 }}>Details</h3>
          <table className="attr-table">
            <tbody>
              <tr><th>Designation</th><td>{val('designation')}</td></tr>
              <tr><th>Department</th><td>{val('department')}</td></tr>
              <tr><th>Location / branch</th><td>{edit ? <select value={form.locationId || ''} onChange={(ev) => setF('locationId', ev.target.value ? Number(ev.target.value) : null)}><option value="">— none —</option>{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select> : (e.location?.name || '—')}</td></tr>
              <tr><th>Phone</th><td>{val('phone')}</td></tr>
              <tr><th>Email</th><td>{val('email')}</td></tr>
              <tr><th>Date of birth</th><td>{edit ? <input type="date" value={form.dob || ''} onChange={(ev) => setF('dob', ev.target.value)} /> : (e.dob ? day(e.dob) : '—')}</td></tr>
              <tr><th>Address<br /><span className="muted small" style={{ fontWeight: 400 }}>(as per address proof)</span></th><td>{edit ? <textarea rows={2} value={form.address || ''} onChange={(ev) => setF('address', ev.target.value)} /> : (e.address || '—')}</td></tr>
              <tr><th>Join date</th><td>{edit ? <input type="date" value={form.joinDate || ''} onChange={(ev) => setF('joinDate', ev.target.value)} /> : (e.joinDate ? day(e.joinDate) : '—')}</td></tr>
              <tr><th>Status</th><td>{edit ? <select value={form.status} onChange={(ev) => setStatus(ev.target.value)}><option value="active">active</option><option value="inactive">inactive</option></select> : e.status}</td></tr>
              {/* The end date only exists for someone who has left, and for them
                  it is required: it is what keeps them on the months they worked
                  and off the ones after. */}
              {(edit ? form.status === 'inactive' : !!e.exitDate) && (
                <tr>
                  <th>End date {edit ? '*' : ''}</th>
                  <td>
                    {edit
                      ? <>
                          {/* No upper bound: someone working out a notice period
                              has a last day that has not arrived yet. */}
                          <input type="date" value={form.exitDate || ''} min={form.joinDate ? String(form.joinDate).slice(0, 10) : undefined}
                            onChange={(ev) => setF('exitDate', ev.target.value)} />
                          <div className="muted small">Their last working day. They stay on attendance and payroll up to {monthName(form.exitDate)}, and drop off the months after it.</div>
                        </>
                      : day(e.exitDate)}
                  </td>
                </tr>
              )}
              <tr><th>Monthly salary</th><td>{edit ? <input type="number" value={form.monthlySalary ?? 0} onChange={(ev) => setF('monthlySalary', ev.target.value)} /> : inr(e.monthlySalary)}</td></tr>
              <tr><th>Leaves / month</th><td>{edit ? <input type="number" value={form.monthlyOffAllowance ?? 0} onChange={(ev) => setF('monthlyOffAllowance', ev.target.value)} /> : num(e.monthlyOffAllowance, 0)}</td></tr>
              <tr><th>Off balance</th><td>{edit ? <input type="number" step="0.5" value={form.offBalance ?? 0} onChange={(ev) => setF('offBalance', ev.target.value)} /> : num(e.offBalance, 1)}</td></tr>
              <tr><th>Usual off day</th><td>{edit ? <select value={form.weeklyOffDay || ''} onChange={(ev) => setF('weeklyOffDay', ev.target.value)}><option value="">—</option>{WEEK_DAYS.map((d) => <option key={d}>{d}</option>)}</select> : (e.weeklyOffDay || '—')}</td></tr>
            </tbody>
          </table>
        </div>

        {/* Identity & bank */}
        <div className="card card-pad">
          <h3 style={{ marginTop: 0 }}>Identity &amp; bank</h3>
          <table className="attr-table">
            <tbody>
              <tr><th>Aadhaar no.</th><td>{val('aadhaarNumber')}</td></tr>
              <tr><th>PAN no.</th><td>{val('panNumber')}</td></tr>
              <tr><th>Bank</th><td>{val('bankName')}</td></tr>
              <tr><th>Account no.</th><td>{val('bankAccount')}</td></tr>
              <tr><th>IFSC</th><td>{val('ifsc')}</td></tr>
              <tr><th>UPI ID</th><td>{val('upiId')}</td></tr>
            </tbody>
          </table>
          <div className="doc-list">
            <DocUploader label="Photo" field="photo" url={e.photoUrl} onUpload={uploadDoc} canEdit={canEdit} />
            <DocUploader label="Aadhaar card" field="aadhaar" url={e.aadhaarDocUrl} onUpload={uploadDoc} canEdit={canEdit} />
            <DocUploader label="Address proof" field="addressProof" url={e.addressProofDocUrl} onUpload={uploadDoc} canEdit={canEdit} />
            <DocUploader label="UPI QR / bank proof" field="upiQr" url={e.upiQrDocUrl} onUpload={uploadDoc} canEdit={canEdit} />
          </div>
        </div>
      </div>

      {/* Advances / loans */}
      <div className="card card-pad" style={{ marginTop: '1rem' }}>
        <div className="section-head" style={{ marginTop: 0 }}>
          <h3 style={{ margin: 0 }}>Advances (recovered monthly)</h3>
          <span className="muted small">{activeLoans.length} active · {inr(loanTotals.remaining)} outstanding · {inr(loanTotals.installment)}/mo recovering</span>
        </div>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Given</th><th>For</th><th className="right">Amount</th><th className="right">Months</th><th className="right">Installment</th><th className="right">Recovered</th><th className="right">Remaining</th><th>Note</th><th>Status</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {loans.map((l) => (
                <tr key={l.id}>
                  <td className="muted small">{dt(l.createdAt)}</td>
                  <td className="muted small">{advanceKind(l.kind)}</td>
                  <td className="right">{inr(l.amount)}</td>
                  <td className="right">{l.months}</td>
                  <td className="right">{inr(l.monthlyInstallment)}</td>
                  <td className="right">{inr(l.recovered)}</td>
                  <td className="right">{inr(Number(l.amount) - Number(l.recovered))}</td>
                  <td className="muted">{l.note || '—'}</td>
                  <td><span className={`badge ${l.status === 'active' ? 'badge-gold' : 'badge-green'}`}>{l.status}</span></td>
                  {canEdit && <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    {l.status === 'active' && <button className="btn btn-outline btn-sm" onClick={() => setAdjustLoan(l)}>Adjust</button>}{' '}
                    {Number(l.recovered) === 0 && <button className="btn btn-danger btn-sm" onClick={() => delLoan(l.id)}>✕</button>}
                  </td>}
                </tr>
              ))}
              {!loans.length && <tr><td colSpan={canEdit ? 10 : 9} className="empty">No advances.</td></tr>}
            </tbody>
            {loans.length > 0 && (
              <tfoot><tr className="total-row">
                <td colSpan={2}><strong>Total</strong></td>
                <td className="right">{inr(loanTotals.amount)}</td>
                <td></td>
                <td className="right">{inr(loanTotals.installment)}/mo</td>
                <td className="right">{inr(loanTotals.recovered)}</td>
                <td className="right"><strong>{inr(loanTotals.remaining)}</strong></td>
                <td colSpan={canEdit ? 3 : 2} className="muted small">outstanding across {activeLoans.length} active advance{activeLoans.length === 1 ? '' : 's'}</td>
              </tr></tfoot>
            )}
          </table>
        </div>
        {canEdit && (
          <div className="flex gap" style={{ marginTop: '.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ maxWidth: 140 }}><label>What for</label>
              <select value={loan.kind} onChange={(ev) => setLoan((p) => ({ ...p, kind: ev.target.value }))}>
                {ADVANCE_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="field" style={{ maxWidth: 130 }}><label>Amount (₹)</label><input type="number" min="0" value={loan.amount} onChange={(ev) => setLoan((p) => ({ ...p, amount: ev.target.value }))} /></div>
            <div className="field" style={{ maxWidth: 100 }}><label>Months</label><input type="number" min="1" value={loan.months} onChange={(ev) => setLoan((p) => ({ ...p, months: ev.target.value }))} /></div>
            <div className="field" style={{ maxWidth: 150 }}><label>Recover from</label><input type="month" value={loan.startMonth} onChange={(ev) => setLoan((p) => ({ ...p, startMonth: ev.target.value }))} /></div>
            <div className="field" style={{ flex: 1, minWidth: 140 }}><label>Note</label><input value={loan.note} onChange={(ev) => setLoan((p) => ({ ...p, note: ev.target.value }))} placeholder="Optional detail" /></div>
            <div className="field"><label>&nbsp;</label><span className="muted small">Installment ≈ {loan.amount && loan.months ? inr(Number(loan.amount) / Number(loan.months)) : '—'}/mo</span></div>
            <button className="btn btn-primary" onClick={addLoan}>Add advance</button>
          </div>
        )}
      </div>

      {/* Ledger: salary advances / items / deductions / bonus (leaves are on Attendance) */}
      <div className="card card-pad" style={{ marginTop: '1rem' }}>
        <div className="section-head" style={{ marginTop: 0 }}>
          <h3 style={{ margin: 0 }}>Salary advances, items &amp; adjustments</h3>
          <span className="muted small">
            {open.length} open · {ledgerTotals.openNet >= 0 ? `−${inr(ledgerTotals.openNet)}` : `+${inr(-ledgerTotals.openNet)}`} at the next payroll
          </span>
        </div>
        <p className="muted small" style={{ marginTop: '.2rem' }}>Leaves/offs are recorded on the <Link to="/attendance">Attendance</Link> sheet.</p>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Date</th><th>Type</th><th className="right">Amount</th><th>Note</th><th>Status</th>{canEdit && <th></th>}</tr></thead>
            <tbody>
              {entries.map((x) => (
                <tr key={x.id}>
                  <td>{day(x.date)}</td>
                  <td><span className={`badge ${kindBadge(x.kind)}`}>{kindLabel(x.kind)}</span></td>
                  <td className="right">{inr(x.amount)}</td>
                  <td className="muted">{x.note || '—'}</td>
                  <td>{x.settledInPayslipId ? <span className="badge badge-green">settled</span> : <span className="badge badge-gray">open</span>}</td>
                  {canEdit && <td className="right">{!x.settledInPayslipId && <button className="btn btn-danger btn-sm" onClick={() => delEntry(x.id)}>✕</button>}</td>}
                </tr>
              ))}
              {!entries.length && <tr><td colSpan={canEdit ? 6 : 5} className="empty">No entries.</td></tr>}
            </tbody>
            {entries.length > 0 && (
              <tfoot><tr className="total-row">
                <td colSpan={2}><strong>Total</strong></td>
                <td className="right"><strong>{ledgerTotals.net >= 0 ? `−${inr(ledgerTotals.net)}` : `+${inr(-ledgerTotals.net)}`}</strong></td>
                <td className="muted small" colSpan={canEdit ? 3 : 2}>
                  advances {inr(ledgerTotals.advances)} · items {inr(ledgerTotals.items)} · other deductions {inr(ledgerTotals.deductions)} · bonus {inr(ledgerTotals.bonus)}
                </td>
              </tr></tfoot>
            )}
          </table>
        </div>
        {canEdit && (
          <div className="flex gap" style={{ marginTop: '.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ maxWidth: 150 }}><label>Date</label><input type="date" value={entry.date} onChange={(ev) => setEntry((p) => ({ ...p, date: ev.target.value }))} /></div>
            <div className="field" style={{ maxWidth: 240 }}><label>Type</label><select value={entry.kind} onChange={(ev) => setEntry((p) => ({ ...p, kind: ev.target.value }))}>{LEDGER_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
            <div className="field" style={{ maxWidth: 130 }}><label>Amount (₹)</label><input type="number" min="0" value={entry.amount} onChange={(ev) => setEntry((p) => ({ ...p, amount: ev.target.value }))} /></div>
            <div className="field" style={{ flex: 1, minWidth: 140 }}><label>Note</label><input value={entry.note} onChange={(ev) => setEntry((p) => ({ ...p, note: ev.target.value }))} /></div>
            <button className="btn btn-primary" onClick={addEntry}>Add entry</button>
          </div>
        )}
      </div>

      {/* Payslips */}
      <div className="card card-pad" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Payslips</h3>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Month</th><th className="right">Fixed</th><th className="right">Off cut</th><th className="right">Advance</th><th className="right">Items</th><th className="right">Loan</th><th className="right">Other</th><th className="right">Net paid</th><th>Paid on</th>{isOwner && <th></th>}</tr></thead>
            <tbody>
              {payslips.map((p) => (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => setViewSlip(p)} title="View / print payslip">
                  <td><strong>{p.month}</strong></td>
                  <td className="right">{inr(p.fixedSalary)}</td>
                  <td className="right">{Number(p.excessOffDeduction) ? `−${inr(p.excessOffDeduction)}` : '—'}</td>
                  <td className="right">{Number(p.salaryAdvance) ? `−${inr(p.salaryAdvance)}` : '—'}</td>
                  <td className="right">{Number(p.itemsTotal) ? `−${inr(p.itemsTotal)}` : '—'}</td>
                  <td className="right">{Number(p.loanInstallment) ? `−${inr(p.loanInstallment)}` : '—'}</td>
                  <td className="right">{Number(p.otherDeductions) ? `−${inr(p.otherDeductions)}` : '—'}{Number(p.bonus) ? ` +${inr(p.bonus)}` : ''}</td>
                  <td className="right"><strong>{inr(p.netPay)}</strong></td>
                  <td className="muted small">{p.paidAt ? dt(p.paidAt) : '—'}</td>
                  {isOwner && (
                    <td className="right">
                      <button className="btn btn-danger btn-sm" title="Delete this payslip (owner only)"
                        onClick={(ev) => { ev.stopPropagation(); delSlip(p); }}>✕</button>
                    </td>
                  )}
                </tr>
              ))}
              {!payslips.length && <tr><td colSpan={isOwner ? 10 : 9} className="empty">No payslips yet — run payroll from the Payroll page.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {viewSlip && <PayslipView slip={viewSlip} employee={e} onClose={() => setViewSlip(null)} />}
      {adjustLoan && <AdjustLoanModal loan={adjustLoan} onClose={() => setAdjustLoan(null)} onSaved={load} />}
    </div>
  );
};

export default EmployeeDetail;
